import { Link } from 'react-router-dom';
import { CheckIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DataTable, DataTableBody, DataTableHead, TableHeader } from '../../ui/DataTable';
import type { ModelReviewAvailable } from '../../../types';
import { SOURCE_LABELS, formatDay, formatLimit, formatPrice } from './reviewUtils';

/**
 * "Available, not in the catalog" (task 1093): provider chat models with no
 * Model row, newest first. "+ Add to catalog" creates the Model row from the
 * provider values; on success the row flips to "In catalog" with "Add config"
 * (the New Model Config form with this Model preselected) and "Edit".
 */

export const availableKey = (entry: Pick<ModelReviewAvailable, 'provider' | 'modelId'>) => `${entry.provider}:${entry.modelId}`;

/** Why "Add to catalog" cannot run for this row, or null when it can. */
export function addBlockedReason(
  entry: ModelReviewAvailable,
  modelsDevUnavailable: boolean,
  providerKnown: boolean,
): string | null {
  if (modelsDevUnavailable) return 'Pricing is unavailable (models.dev did not respond); prices are required on a Model.';
  if (!entry.pricing || entry.pricing.base.inputPriceMTok === null || entry.pricing.base.outputPriceMTok === null) {
    return 'No pricing available from models.dev; prices are required on a Model.';
  }
  if (entry.contextWindow === null || entry.maxOutputTokens === null) {
    return 'No context window or max output from the provider sources; limits are required on a Model.';
  }
  if (!providerKnown) return `No Model Provider with key "${entry.provider}" in the catalog.`;
  return null;
}

const ADD_BUTTON =
  'inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const LINK_BUTTON =
  'inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700';

interface ReviewAvailableTableProps {
  rows: ModelReviewAvailable[];
  modelsDevUnavailable: boolean;
  canWrite: boolean;
  providerIdByKey: Map<string, string>;
  /** Rows added on this page: available key → the created Model's id. */
  added: Map<string, string>;
  busyKey: string | null;
  onAdd: (entry: ModelReviewAvailable) => void;
}

export function ReviewAvailableTable({
  rows,
  modelsDevUnavailable,
  canWrite,
  providerIdByKey,
  added,
  busyKey,
  onAdd,
}: ReviewAvailableTableProps) {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No provider models missing from the catalog.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <DataTable>
        <DataTableHead>
          <TableHeader>Model</TableHeader>
          <TableHeader>Released</TableHeader>
          <TableHeader>Context window</TableHeader>
          <TableHeader>Max output</TableHeader>
          <TableHeader>Input $/MTok</TableHeader>
          <TableHeader>Output $/MTok</TableHeader>
          <TableHeader>Cache r / w</TableHeader>
          <TableHeader>
            <span className="sr-only">Actions</span>
          </TableHeader>
        </DataTableHead>
        <DataTableBody>
          {rows.map((entry) => {
            const key = availableKey(entry);
            const createdId = added.get(key);
            const blocked = addBlockedReason(entry, modelsDevUnavailable, providerIdByKey.has(entry.provider));
            const pricing = entry.pricing;
            return (
              <tr key={key} data-testid={`available-row-${entry.modelId}`}>
                <td className="px-3 py-3 align-top">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-sm text-gray-900 dark:text-white">{entry.modelId}</span>
                    {entry.newerThanCatalog && (
                      <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        newer
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {entry.provider}
                    {entry.displayName ? ` · ${entry.displayName}` : ''}
                  </div>
                </td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">{formatDay(entry.releasedAt)}</td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">{formatLimit(entry.contextWindow)}</td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">{formatLimit(entry.maxOutputTokens)}</td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">
                  {pricing ? formatPrice(pricing.base.inputPriceMTok) : <span className="text-xs italic text-gray-400">no suggestion</span>}
                  {pricing?.tiers && pricing.tiers.length > 0 && (
                    <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">tiers</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">
                  {pricing ? formatPrice(pricing.base.outputPriceMTok) : <span className="text-xs italic text-gray-400">no suggestion</span>}
                </td>
                <td className="px-3 py-3 align-top text-sm text-gray-700 dark:text-gray-300">
                  {pricing ? (
                    <>
                      {formatPrice(pricing.cache.readPriceMTok)} / {formatPrice(pricing.cache.writePriceMTok)}
                      <div className="text-xs text-gray-400 dark:text-gray-500">{SOURCE_LABELS[pricing.source] ?? pricing.source}</div>
                    </>
                  ) : (
                    <span className="text-xs italic text-gray-400">no suggestion</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top text-right">
                  {createdId ? (
                    <div className="flex flex-col items-end gap-1">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-300">
                        <CheckIcon className="h-3.5 w-3.5" />
                        In catalog
                      </span>
                      <div className="flex gap-1">
                        <Link to={`/model-catalog?model=${encodeURIComponent(createdId)}&addConfig=1`} className={LINK_BUTTON}>
                          <PlusIcon className="h-3.5 w-3.5" />
                          Add config
                        </Link>
                        <Link to={`/model-catalog/models/${createdId}?edit=1`} className={LINK_BUTTON} aria-label={`Edit ${entry.modelId}`}>
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit
                        </Link>
                      </div>
                    </div>
                  ) : canWrite ? (
                    <div className="flex flex-col items-end gap-1">
                      <button
                        type="button"
                        disabled={blocked !== null || busyKey === key}
                        title={blocked ?? undefined}
                        onClick={() => onAdd(entry)}
                        className={ADD_BUTTON}
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        {busyKey === key ? 'Adding…' : 'Add to catalog'}
                      </button>
                      {blocked && <span className="max-w-[16rem] text-xs text-gray-500 dark:text-gray-400">{blocked}</span>}
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </DataTableBody>
      </DataTable>
    </div>
  );
}
